import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { burgersData, burgerMenusData, sectionImages } from '../data/menuData';
import { Flame, Leaf, Star, Sparkles } from 'lucide-react';

const BURGER_BG = sectionImages.burgers;

const BurgersSection = () => {
  const { language, t } = useLanguage();

  const renderBadges = (item) => {
    const badges = [];
    if (item.isPopular) badges.push({ icon: <Star className="w-4 h-4 text-amber-400 fill-amber-400" />, key: 'popular' });
    if (item.isSpicy) badges.push({ icon: <Flame className="w-4 h-4 text-orange-500" />, key: 'spicy' });
    if (item.isVegetarian) badges.push({ icon: <Leaf className="w-4 h-4 text-emerald-500" />, key: 'vegetarian' });
    
    if (badges.length === 0) return null;
    return (
      <div className="flex items-center gap-1.5 ml-2">
        {badges.map((b) => <span key={b.key}>{b.icon}</span>)}
      </div>
    );
  };

  return (
    <section 
      id="burgers" 
      className="relative overflow-hidden"
      data-testid="burgers-section"
    >
      {/* Premium Section Header */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${BURGER_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-hanse-dark/50 via-hanse-dark/60 to-hanse-dark/95" />
        
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex items-center gap-2 mb-4 bg-gradient-to-r from-orange-500/20 to-orange-600/10 backdrop-blur-sm px-4 py-2 rounded-full border border-orange-400/30"
          >
            <Sparkles className="w-5 h-5 text-orange-400" />
            <span className="text-orange-300 font-semibold text-sm uppercase tracking-wider">
              {language === 'de' ? 'Saftig & Knusprig' : 'Juicy & Crispy'}
            </span>
            <Sparkles className="w-5 h-5 text-orange-400" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3"
          >
            {t('menu.burgers.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-white/80 text-lg md:text-xl max-w-2xl"
          >
            {t('menu.burgers.subtitle')}
          </motion.p>
        </div>
      </div>

      {/* Premium Menu Content */}
      <div className="bg-gradient-to-b from-hanse-dark to-hanse-navy py-10 md:py-14">
        <div className="container mx-auto px-4 max-w-6xl">
          
          {/* Burger Menus - Premium Highlight Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h3 className="font-heading text-2xl font-bold text-white mb-6 text-center flex items-center justify-center gap-3">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-amber-400/50"></span>
              {t('menu.burgers.menus')}
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-amber-400/50"></span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {burgerMenusData.map((menu, index) => (
                <motion.div
                  key={menu.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative bg-gradient-to-br from-amber-500/10 to-amber-600/5 backdrop-blur-sm border border-amber-400/20 rounded-xl p-4 text-center hover:border-amber-400/40 transition-all group"
                  data-testid={`burger-menu-${menu.id}`}
                >
                  <h4 className="font-bold text-white text-sm mb-1 group-hover:text-amber-300 transition-colors">
                    {language === 'de' ? menu.name : menu.nameEn}
                  </h4>
                  <p className="text-white/50 text-xs mb-3">
                    {language === 'de' ? menu.description : menu.descriptionEn}
                  </p>
                  <span className="text-amber-400 font-bold text-lg">€{menu.price.toFixed(2)}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* All Burgers - Premium Grid */}
          <div className="grid md:grid-cols-2 gap-3">
            {burgersData.map((burger, index) => (
              <motion.div
                key={burger.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: Math.min(index * 0.03, 0.2) }}
                className="group relative bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 hover:border-orange-400/30 hover:bg-white/10 transition-all duration-300"
                data-testid={`burger-item-${burger.id}`}
              >
                {/* Popular badge */}
                {burger.isPopular && (
                  <div className="absolute -top-2 right-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                    <Star className="w-3 h-3 inline mr-1 fill-white" />
                    {language === 'de' ? 'Beliebt' : 'Popular'}
                  </div>
                )}

                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <h3 className="font-heading text-lg font-bold text-white group-hover:text-orange-400 transition-colors">
                        {language === 'de' ? burger.name : burger.nameEn}
                      </h3>
                      {renderBadges(burger)}
                    </div>
                    <p className="text-white/50 text-sm mt-1 leading-relaxed">
                      {language === 'de' ? burger.description : burger.descriptionEn}
                    </p>
                  </div>
                  <span className="font-bold text-amber-400 text-xl">
                    €{burger.price.toFixed(2)}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BurgersSection;
